/**
 * TransformGizmo — reusable Blender-style combined transform gizmo.
 *
 * One widget, constant screen size, anchored at the bound target's center:
 *  - MOVE: three axis arrows (X red / Y green / Z blue) — drag translates
 *    along that axis.
 *  - ROTATE: three axis arcs (half-tori re-oriented every frame so the arc
 *    faces the camera, like Blender) — drag rotates around that axis.
 *  - SCALE: cubes past the arrow tips scale a SINGLE axis; three squares
 *    sitting between the axis pairs scale the two spanned axes (XY / XZ /
 *    YZ). Both scale around the center.
 *
 * In 2D (orthographic) views the widget switches to a VIEW-SPACE variant —
 * detected automatically from the active camera, overridable via `setMode`:
 * two arrows along the frame's X/Y, one full ring rotating around the view
 * axis, per-axis scale cubes and the in-plane scale square.
 *
 * The gizmo owns rendering, picking and all drag MATH; everything
 * target-specific lives in a {@link TransformGizmoBinding}: the frame
 * (center + orientation), the axis SPACE policy (object / world / view),
 * the handle whitelist, and how the CUMULATIVE deltas the gizmo computes
 * are applied (direct Object3D mutation, reactive-property writes, …).
 * Owners usually drive the gizmo through TransformGizmoInteraction rather
 * than calling the drag API directly.
 *
 * Coordinate convention: all mouse parameters are **pixel coordinates**
 * (clientX - rect.left, clientY - rect.top), matching InputHandler's mouse.
 */

import * as THREE from 'three';
import { Utils } from '../../../../utils';
import { Viewer } from '../../../../viewer/viewer';
import type { GizmoAxis, GizmoDragModifiers, TransformGizmoBinding } from './TransformGizmoBinding';

/**
 * What the gizmo needs of the view it lives in. The Potree viewer is one
 * host ({@link viewerGizmoHost}); a standalone three.js view (the Object
 * Editor's 3D preview) is another.
 */
export interface TransformGizmoHost {
  getCamera(): THREE.Camera;
  getDomElement(): HTMLElement;
  /** Surface point under the cursor the free-move sphere snaps onto in 3D; null = plain camera-plane drag. */
  pickSurface?(pixelMouse: THREE.Vector2): THREE.Vector3 | null;
}

/** The Potree viewer as gizmo host: active camera, render canvas, point-cloud snapping. */
export function viewerGizmoHost(viewer: Viewer): TransformGizmoHost {
  return {
    getCamera: () => viewer.scene.getActiveCamera(),
    getDomElement: () => viewer.renderer.domElement,
    pickSurface: (pixelMouse) => {
      const camera = viewer.scene.getActiveCamera();
      const I = Utils.getMousePointCloudIntersection(pixelMouse, camera, viewer, viewer.scene.pointclouds, { pickClipped: false });
      return I && 'location' in I ? I.location.clone() : null;
    },
  };
}

/** Handle ids, also used by pick volumes: `translation.x|y|z`, `rotation.x|y|z|view`, `scale.x|y|z|xy|xz|yz`. */
type HandleKind = 'translation' | 'rotation' | 'scale';

interface GizmoHandle {
  name: string;
  kind: HandleKind;
  /** Unit axis (translation/rotation/axis-scale) or plane normal (plane-scale). */
  axis: THREE.Vector3;
  /** For scale handles: the axes being scaled — one entry for the axis
   *  cubes, two for the plane squares ('xy' → x & y). */
  planeAxes: GizmoAxis[];
  node: THREE.Object3D;
  /** Materials tracking hover state (opacity + color whitened toward white). */
  materials: { material: THREE.MeshBasicMaterial | THREE.LineBasicMaterial; baseColor: THREE.Color }[];
}

/** Screen radius (px) of the gizmo's rotation arcs — everything scales off this. */
const SCREEN_RADIUS_PX = 80;
/** Hover feedback, Blender-like: the hovered handle jumps to full opacity
 *  and its color whitens strongly; the rest stay dimmed. */
const IDLE_OPACITY = 0.55;
const HOVER_OPACITY = 1.0;
const HOVER_WHITEN = 0.55;
const WHITE = new THREE.Color(0xffffff);

/**
 * Brand-themed axis colors (there is no brand red/green/blue triad, so the
 * classic RGB=XYZ convention gives way to the palette): X = french violet
 * (the wall editor's edit/transform identity), Y = oil blue, Z — and with
 * it the 2D rotation ring — the primary brand green. The plane squares
 * inherit their normal axis' color as before.
 */
const AXIS_COLORS: Record<GizmoAxis, number> = {
  x: 0x854dd6,
  y: 0x7edd64,
  z: 0x269fc6,
};
const AXIS_VECTORS: Record<GizmoAxis, THREE.Vector3> = {
  x: new THREE.Vector3(1, 0, 0),
  y: new THREE.Vector3(0, 1, 0),
  z: new THREE.Vector3(0, 0, 1),
};

// Unit-space layout (multiplied by the per-frame screen scale).
const ARROW_SHAFT_RADIUS = 0.02;
/** Shafts start AT the center sphere's surface so the arrows visually
 *  connect to it (matches CENTER_SPHERE_RADIUS below). */
const ARROW_SHAFT_FROM = 0.12;
const ARROW_SHAFT_TO = 1.08;
const ARROW_TIP_RADIUS = 0.07;
const ARROW_TIP_LENGTH = 0.2;
const RING_RADIUS = 1.0;
const RING_TUBE = 0.02;
/** The visible rotation arc — half a circle, re-aimed at the camera per frame. */
const RING_ARC = Math.PI;
const SQUARE_SIZE = 0.2;
/** Diagonal offset of each plane-scale square along its two axes. */
const SQUARE_OFFSET = 0.42;
/** Single-axis scale cube, sitting past the arrow tip on the same axis. */
const SCALE_CUBE_SIZE = 0.13;
const SCALE_CUBE_OFFSET = 1.48;
// Invisible pick-volume sizes (generous, hit-tested by raycast).
const PICK_ARROW_RADIUS = 0.11;
const PICK_RING_TUBE = 0.09;
const PICK_SQUARE_SIZE = 0.34;
const PICK_SCALE_CUBE_SIZE = 0.28;

/** View-axis rotation ring (2D mode). The app's 2D views are top-down, so
 *  the view axis coincides with world Z — the ring wears the Z color. */
const VIEW_RING_COLOR = AXIS_COLORS.z;
/** Center free-move sphere: in 2D it drags freely in the view plane; in 3D
 *  the target FOLLOWS the point cloud under the cursor (placement logic). */
const FREE_HANDLE_NAME = 'translation.free';
const CENTER_SPHERE_RADIUS = 0.12;
const PICK_CENTER_SPHERE_RADIUS = 0.24;
const CENTER_SPHERE_COLOR = 0xdddddd;
/** Handles shown in the view-space (2D) variant. */
const VIEW_MODE_HANDLES = new Set(['translation.x', 'translation.y', 'translation.free', 'rotation.view', 'scale.x', 'scale.y', 'scale.xy']);

/** Per-drag baseline. All fields frozen at startDrag; processDrag derives the
 *  cumulative delta from them each frame (never from the previous frame). */
interface DragState {
  handle: GizmoHandle;
  /** Gizmo center at drag start. */
  center: THREE.Vector3;
  /** Grab point projected onto the drag line/plane. */
  pivot: THREE.Vector3;
  /** World-space handle axis at drag start (rotation axis / plane normal / line dir). */
  worldAxis: THREE.Vector3;
  plane: THREE.Plane;
  line: THREE.Line3 | null;
  /** Free-move sphere in 3D: the center SNAPS onto the point cloud under
   *  the cursor (no drag plane, no preserved offset — an offset against a
   *  3D surface drifts apparently with the surface's depth). */
  pcFollow: boolean;
  /** |pivot - center| for scale factor derivation. */
  fromLength: number;
  /** Rotation: previous frame's direction from center, and the summed sweep —
   *  incremental accumulation lets sweeps exceed ±180°. */
  prevDir: THREE.Vector3;
  accumAngle: number;
  moved: boolean;
}

export class TransformGizmo {
  readonly scene: THREE.Scene;

  private host: TransformGizmoHost;
  private root: THREE.Group;
  private binding: TransformGizmoBinding | null = null;

  private handles = new Map<string, GizmoHandle>();
  private pickVolumes: THREE.Mesh[] = [];
  /** Rotation arc meshes + their lookAt base orientation, for the per-frame
   *  spin that keeps the visible arc facing the camera. */
  private ringMeshes = new Map<string, { mesh: THREE.Mesh; baseQuaternion: THREE.Quaternion }>();

  private activeHandle: GizmoHandle | null = null;
  private drag: DragState | null = null;

  /** 3D (full set) vs view-space 2D variant; 'auto' follows the active
   *  camera (orthographic = the app's 2D views). */
  private mode: 'auto' | '3d' | 'view' = 'auto';
  private viewModeActive = false;
  /** Handle whitelist last read from the binding (reference-compared). */
  private enabledFilter: ReadonlyArray<string> | null = null;

  constructor(host: TransformGizmoHost) {
    this.host = host;
    this.scene = new THREE.Scene();
    this.root = new THREE.Group();
    this.root.name = 'transform_gizmo';
    this.scene.add(this.root);

    for (const axis of ['x', 'y', 'z'] as const) {
      this.buildArrow(axis);
      this.buildRing(axis);
      this.buildScaleCube(axis);
    }
    this.buildSquare('xy');
    this.buildSquare('xz');
    this.buildSquare('yz');
    this.buildViewRing();
    this.buildCenterSphere();
    this.applyModeVisibility();
  }

  // ─── Binding ────────────────────────────────────────────────────

  setBinding(binding: TransformGizmoBinding | null): void {
    if (this.binding === binding) return;
    this.binding?.onUnbound?.();
    this.binding = binding;
    binding?.onBound?.();
  }

  getBinding(): TransformGizmoBinding | null {
    return this.binding;
  }

  /** Force the 3D or view-space variant, or let the camera decide (default). */
  setMode(mode: 'auto' | '3d' | 'view'): void {
    this.mode = mode;
  }

  // ─── Handle hover detection ─────────────────────────────────────

  /** Raycast the pick volumes; highlights and returns the hit handle name. */
  updateActiveHandle(pixelMouse: THREE.Vector2): string | null {
    if (!this.binding || !this.scene.visible) return null;
    if (this.drag) return this.drag.handle.name;

    const intersects = this.raycastPickVolumes(pixelMouse);
    const handleName = intersects.length > 0 ? String(intersects[0].object.userData.gizmoHandle) : null;
    this.setHoverHandle(handleName ? (this.handles.get(handleName) ?? null) : null);
    return handleName;
  }

  /** World point where the mouse ray hits `handleName`'s pick volume (drag start anchor). */
  getHandleIntersection(handleName: string, pixelMouse: THREE.Vector2): THREE.Vector3 | null {
    for (const hit of this.raycastPickVolumes(pixelMouse)) {
      if (hit.object.userData.gizmoHandle === handleName) return hit.point;
    }
    const handle = this.handles.get(handleName);
    return handle ? handle.node.getWorldPosition(new THREE.Vector3()) : null;
  }

  // ─── Drag API ───────────────────────────────────────────────────

  startDrag(handleName: string, pixelMouse: THREE.Vector2, worldPoint: THREE.Vector3): boolean {
    const handle = this.handles.get(handleName);
    if (!handle || !this.binding) return false;

    const camera = this.host.getCamera();
    const center = this.root.getWorldPosition(new THREE.Vector3());
    // Frozen at drag start: with cumulative re-application the baseline
    // frame must not drift with the in-progress transform.
    const worldAxis = handle.axis.clone().applyQuaternion(this.root.quaternion).normalize();

    let plane: THREE.Plane;
    let line: THREE.Line3 | null = null;
    let pivot: THREE.Vector3;
    let pcFollow = false;
    if (handle.name === FREE_HANDLE_NAME) {
      // Free move: 2D drags on the camera-facing plane; 3D SNAPS the center
      // onto the point cloud under the cursor (the placement tools' logic)
      // with a camera-plane fallback when the grab isn't over the cloud.
      pcFollow = !this.viewModeActive && this.pointCloudHit(pixelMouse) !== null;
      plane = new THREE.Plane().setFromNormalAndCoplanarPoint(TransformGizmo.towardCamera(camera, worldPoint), worldPoint);
      pivot = worldPoint.clone();
    } else if (handle.kind === 'translation' || (handle.kind === 'scale' && handle.planeAxes.length === 1)) {
      // Axis-line drags: mouse deltas are measured on a camera-facing plane
      // containing the line. Translation's line runs through the grab point
      // (deltas move the target); single-axis scale's runs through the
      // CENTER (distance ratios scale it).
      const lineAnchor = handle.kind === 'translation' ? worldPoint.clone() : center.clone();
      line = new THREE.Line3(lineAnchor, lineAnchor.clone().add(worldAxis));
      const camOnLine = line.closestPointToPoint(camera.position, false, new THREE.Vector3());
      const normal = TransformGizmo.towardCamera(camera, camOnLine);
      plane = new THREE.Plane().setFromNormalAndCoplanarPoint(normal, worldPoint);
      pivot = line.closestPointToPoint(worldPoint, false, new THREE.Vector3());
    } else {
      // Rotation and plane-scale both live on the plane through the gizmo
      // center: rotation's plane is ⟂ the rotation axis, scale's is the
      // spanned plane (⟂ its normal axis). Project the grab point onto it.
      plane = new THREE.Plane().setFromNormalAndCoplanarPoint(worldAxis, center);
      pivot = plane.projectPoint(worldPoint, new THREE.Vector3());
    }

    const fromCenter = pivot.clone().sub(center);
    this.drag = {
      handle,
      center,
      pivot,
      worldAxis,
      plane,
      line,
      pcFollow,
      fromLength: fromCenter.length(),
      prevDir: fromCenter.lengthSq() > 1e-12 ? fromCenter.normalize() : new THREE.Vector3(1, 0, 0),
      accumAngle: 0,
      moved: false,
    };
    this.activeHandle = handle;
    this.binding.beginDrag(handleName);
    return true;
  }

  processDrag(pixelMouse: THREE.Vector2, modifiers?: GizmoDragModifiers): void {
    const drag = this.drag;
    if (!drag || !this.binding) return;

    const override = modifiers?.override ?? false;

    // Free-move sphere in 3D: the center SNAPS onto the cloud point under
    // the cursor — expressed as the cumulative delta from the baseline
    // center, so appliers stay idempotent. Frames where the cursor misses
    // the cloud keep the last applied position.
    if (drag.pcFollow) {
      const hit = this.pointCloudHit(pixelMouse);
      if (hit) {
        this.binding.applyTranslation(hit.sub(drag.center), override);
        drag.moved = true;
      }
      return;
    }

    const I = this.mouseRaycaster(pixelMouse).ray.intersectPlane(drag.plane, new THREE.Vector3());
    if (!I) return;

    const { handle } = drag;

    if (handle.kind === 'translation') {
      // Axis arrows constrain to their line; the free sphere takes the full
      // in-plane delta.
      const onTarget = drag.line ? drag.line.closestPointToPoint(I, false, new THREE.Vector3()) : I;
      this.binding.applyTranslation(new THREE.Vector3().subVectors(onTarget, drag.pivot), override);
      drag.moved = true;
      return;
    }

    if (handle.kind === 'scale') {
      // Cumulative factor: the cursor's distance from the center over the
      // grab distance — along the axis line for cubes, within the spanned
      // plane for squares. The target's center stays put.
      if (drag.fromLength < 1e-9) return;
      const current = drag.line ? drag.line.closestPointToPoint(I, false, new THREE.Vector3()) : I;
      const factor = current.clone().sub(drag.center).length() / drag.fromLength;
      this.binding.applyScale(handle.planeAxes, factor, override);
      drag.moved = true;
      return;
    }

    // Rotation: accumulate the signed per-frame sweep so drags past ±180°
    // keep counting, then re-apply the TOTAL.
    const dir = I.clone().sub(drag.center);
    if (dir.lengthSq() < 1e-12) return;
    dir.normalize();
    let increment = Math.acos(THREE.MathUtils.clamp(drag.prevDir.dot(dir), -1, 1));
    increment *= Math.sign(drag.prevDir.clone().cross(dir).dot(drag.worldAxis)) || 0;
    if (!Number.isNaN(increment)) {
      drag.accumAngle += increment;
      drag.prevDir.copy(dir);
      this.binding.applyRotation(drag.worldAxis, drag.accumAngle, override);
      drag.moved = true;
    }
  }

  /** Geometric drag teardown. Returns whether any delta was applied — the
   *  CALLER decides commit vs cancel on the binding. */
  endDrag(): boolean {
    const moved = this.drag?.moved ?? false;
    this.drag = null;
    this.setHoverHandle(null);
    return moved;
  }

  isDragging(): boolean {
    return this.drag !== null;
  }

  // ─── Frame update (call every frame while the owning tool is active) ─

  update(camera: THREE.Camera, domElement: HTMLElement): void {
    const frame = this.binding?.getFrame() ?? null;
    if (!this.binding || !frame) {
      this.scene.visible = false;
      return;
    }
    this.scene.visible = true;

    // 2D (orthographic) views get the view-space variant; don't flip
    // mid-drag so the grabbed handle's frame stays stable. The binding's
    // handle whitelist is reference-compared so per-frame reads stay cheap.
    const viewMode = this.mode === 'auto' ? camera.type === 'OrthographicCamera' : this.mode === 'view';
    const filter = this.binding.getEnabledHandles();
    if ((viewMode !== this.viewModeActive || filter !== this.enabledFilter) && !this.drag) {
      this.viewModeActive = viewMode;
      this.enabledFilter = filter;
      this.applyModeVisibility();
    }

    // Anchor at the binding's frame, at a constant screen size (the gizmo
    // never wraps the object). Orientation follows the binding's SPACE
    // policy; 'auto' = object-aligned in 3D, camera-aligned in view mode.
    this.root.position.copy(frame.position);
    const space = this.binding.getSpace();
    const resolved = space === 'auto' ? (this.viewModeActive ? 'view' : 'object') : space;
    if (resolved === 'view') camera.getWorldQuaternion(this.root.quaternion);
    else if (resolved === 'world') this.root.quaternion.identity();
    else this.root.quaternion.copy(frame.quaternion);

    const distance = camera.position.distanceTo(frame.position);
    const pr = Utils.projectedRadius(1, camera, distance, domElement.clientWidth, domElement.clientHeight);
    if (pr > 1e-9) this.root.scale.setScalar(SCREEN_RADIUS_PX / pr);

    this.root.updateMatrixWorld(true);

    // Aim each rotation arc's midpoint at the camera (skip mid-drag so the
    // grabbed arc doesn't slide under the cursor; moot in view mode — the
    // axis arcs are hidden and the view ring is a full circle).
    if (!this.drag && !this.viewModeActive) this.faceRingsToCamera(camera);
  }

  // ─── Dispose ────────────────────────────────────────────────────

  dispose(): void {
    this.setBinding(null);
    this.scene.traverse((obj) => {
      const mesh = obj as THREE.Mesh;
      if (mesh.geometry) mesh.geometry.dispose();
      const material = (mesh as { material?: THREE.Material | THREE.Material[] }).material;
      if (Array.isArray(material)) material.forEach((m) => m.dispose());
      else material?.dispose();
    });
    this.scene.clear();
    this.handles.clear();
    this.ringMeshes.clear();
    this.pickVolumes = [];
  }

  // ─── Handle construction ────────────────────────────────────────

  private makeMaterial(color: number): THREE.MeshBasicMaterial {
    return new THREE.MeshBasicMaterial({
      color,
      opacity: IDLE_OPACITY,
      transparent: true,
      depthTest: false,
      depthWrite: false,
      side: THREE.DoubleSide,
    });
  }

  private makePickMaterial(): THREE.MeshBasicMaterial {
    // Invisible but raycastable (material visibility doesn't gate raycasts).
    // DoubleSide matters: the raycaster culls backface hits per material.side,
    // and flat pick volumes (the plane-scale squares) must hit from BOTH
    // sides — the gizmo is viewed from every direction.
    return new THREE.MeshBasicMaterial({ visible: false, depthTest: false, depthWrite: false, side: THREE.DoubleSide });
  }

  private registerPickVolume(mesh: THREE.Mesh, handleName: string): void {
    mesh.name = `${handleName}.pick_volume`;
    mesh.userData.gizmoHandle = handleName;
    this.pickVolumes.push(mesh);
  }

  private addHandle(handle: GizmoHandle): void {
    this.handles.set(handle.name, handle);
    this.root.add(handle.node);
  }

  /** Axis arrow (shaft + cone tip) for translation. */
  private buildArrow(axis: GizmoAxis): void {
    const name = `translation.${axis}`;
    const node = new THREE.Object3D();
    node.name = name;
    // Author along +Z, then aim at the axis (lookAt maps +Z onto it).
    node.lookAt(AXIS_VECTORS[axis]);

    const material = this.makeMaterial(AXIS_COLORS[axis]);
    const shaftLength = ARROW_SHAFT_TO - ARROW_SHAFT_FROM;
    const shaft = new THREE.Mesh(new THREE.CylinderGeometry(ARROW_SHAFT_RADIUS, ARROW_SHAFT_RADIUS, shaftLength, 12), material);
    shaft.rotation.x = Math.PI / 2; // cylinder is Y-authored → lay along local Z
    shaft.position.z = ARROW_SHAFT_FROM + shaftLength / 2;
    shaft.renderOrder = 10;
    node.add(shaft);

    const tip = new THREE.Mesh(new THREE.ConeGeometry(ARROW_TIP_RADIUS, ARROW_TIP_LENGTH, 16), material);
    tip.rotation.x = Math.PI / 2;
    tip.position.z = ARROW_SHAFT_TO + ARROW_TIP_LENGTH / 2;
    tip.renderOrder = 10;
    node.add(tip);

    const pickLength = ARROW_SHAFT_TO + ARROW_TIP_LENGTH - ARROW_SHAFT_FROM;
    const pick = new THREE.Mesh(new THREE.CylinderGeometry(PICK_ARROW_RADIUS, PICK_ARROW_RADIUS, pickLength, 8), this.makePickMaterial());
    pick.rotation.x = Math.PI / 2;
    pick.position.z = ARROW_SHAFT_FROM + pickLength / 2;
    this.registerPickVolume(pick, name);
    node.add(pick);

    this.addHandle({ name, kind: 'translation', axis: AXIS_VECTORS[axis].clone(), planeAxes: [], node, materials: [{ material, baseColor: new THREE.Color(AXIS_COLORS[axis]) }] });
  }

  /** Single-axis scale cube, past the arrow tip on the same axis. */
  private buildScaleCube(axis: GizmoAxis): void {
    const name = `scale.${axis}`;
    const node = new THREE.Object3D();
    node.name = name;
    node.position.copy(AXIS_VECTORS[axis]).multiplyScalar(SCALE_CUBE_OFFSET);

    const material = this.makeMaterial(AXIS_COLORS[axis]);
    const cube = new THREE.Mesh(new THREE.BoxGeometry(SCALE_CUBE_SIZE, SCALE_CUBE_SIZE, SCALE_CUBE_SIZE), material);
    cube.name = `${name}.handle`;
    cube.renderOrder = 10;
    node.add(cube);

    const pick = new THREE.Mesh(new THREE.BoxGeometry(PICK_SCALE_CUBE_SIZE, PICK_SCALE_CUBE_SIZE, PICK_SCALE_CUBE_SIZE), this.makePickMaterial());
    this.registerPickVolume(pick, name);
    node.add(pick);

    this.addHandle({ name, kind: 'scale', axis: AXIS_VECTORS[axis].clone(), planeAxes: [axis], node, materials: [{ material, baseColor: new THREE.Color(AXIS_COLORS[axis]) }] });
  }

  /** Camera-facing rotation arc for one axis. */
  private buildRing(axis: GizmoAxis): void {
    const name = `rotation.${axis}`;
    const node = new THREE.Object3D();
    node.name = name;

    const material = this.makeMaterial(AXIS_COLORS[axis]);
    const arc = new THREE.Mesh(new THREE.TorusGeometry(RING_RADIUS, RING_TUBE, 8, 48, RING_ARC), material);
    arc.name = `${name}.handle`;
    // Torus is XY-authored (normal +Z); aim the normal at the ring's axis.
    arc.lookAt(AXIS_VECTORS[axis]);
    arc.renderOrder = 9;
    node.add(arc);

    const pick = new THREE.Mesh(new THREE.TorusGeometry(RING_RADIUS, PICK_RING_TUBE, 6, 24, RING_ARC), this.makePickMaterial());
    this.registerPickVolume(pick, name);
    arc.add(pick); // child of the arc → follows the camera-facing spin

    this.ringMeshes.set(name, { mesh: arc, baseQuaternion: arc.quaternion.clone() });
    this.addHandle({ name, kind: 'rotation', axis: AXIS_VECTORS[axis].clone(), planeAxes: [], node, materials: [{ material, baseColor: new THREE.Color(AXIS_COLORS[axis]) }] });
  }

  /**
   * Full-circle rotation ring for the view-space (2D) variant — rotates the
   * target around the VIEW axis. Only visible in view mode.
   */
  private buildViewRing(): void {
    const name = 'rotation.view';
    const node = new THREE.Object3D();
    node.name = name;

    const material = this.makeMaterial(VIEW_RING_COLOR);
    const ring = new THREE.Mesh(new THREE.TorusGeometry(RING_RADIUS, RING_TUBE, 8, 64), material);
    ring.name = `${name}.handle`;
    ring.renderOrder = 9;
    node.add(ring);

    const pick = new THREE.Mesh(new THREE.TorusGeometry(RING_RADIUS, PICK_RING_TUBE, 6, 32), this.makePickMaterial());
    this.registerPickVolume(pick, name);
    node.add(pick);

    this.addHandle({ name, kind: 'rotation', axis: AXIS_VECTORS.z.clone(), planeAxes: [], node, materials: [{ material, baseColor: new THREE.Color(VIEW_RING_COLOR) }] });
  }

  /** Center free-move sphere — full in-plane translation in 2D, point-cloud
   *  follow in 3D. Neutral color (it moves on every axis at once). */
  private buildCenterSphere(): void {
    const name = FREE_HANDLE_NAME;
    const node = new THREE.Object3D();
    node.name = name;

    const material = this.makeMaterial(CENTER_SPHERE_COLOR);
    const sphere = new THREE.Mesh(new THREE.SphereGeometry(CENTER_SPHERE_RADIUS, 24, 16), material);
    sphere.name = `${name}.handle`;
    sphere.renderOrder = 11; // above the arrows crossing the center
    node.add(sphere);

    const pick = new THREE.Mesh(new THREE.SphereGeometry(PICK_CENTER_SPHERE_RADIUS, 12, 8), this.makePickMaterial());
    this.registerPickVolume(pick, name);
    node.add(pick);

    this.addHandle({ name, kind: 'translation', axis: AXIS_VECTORS.z.clone(), planeAxes: [], node, materials: [{ material, baseColor: new THREE.Color(CENTER_SPHERE_COLOR) }] });
  }

  /** Show the full 3D handle set or the view-space 2D subset, further
   *  narrowed by the binding's whitelist. */
  private applyModeVisibility(): void {
    for (const handle of this.handles.values()) {
      const inMode = this.viewModeActive ? VIEW_MODE_HANDLES.has(handle.name) : handle.name !== 'rotation.view';
      handle.node.visible = inMode && (!this.enabledFilter || this.enabledFilter.includes(handle.name));
    }
  }

  /** Plane-scale square between an axis pair, colored by the plane's normal axis. */
  private buildSquare(plane: 'xy' | 'xz' | 'yz'): void {
    const name = `scale.${plane}`;
    const [a, b] = plane.split('') as GizmoAxis[];
    const normalAxis = (['x', 'y', 'z'] as const).find((ax) => ax !== a && ax !== b) ?? 'z';

    const node = new THREE.Object3D();
    node.name = name;
    // PlaneGeometry is XY-authored (normal +Z); aim the normal at the plane's
    // normal axis, then push the square out along the two spanned axes.
    node.lookAt(AXIS_VECTORS[normalAxis]);
    const offset = new THREE.Vector3().addScaledVector(AXIS_VECTORS[a], SQUARE_OFFSET).addScaledVector(AXIS_VECTORS[b], SQUARE_OFFSET);
    node.position.copy(offset);

    const material = this.makeMaterial(AXIS_COLORS[normalAxis]);
    const square = new THREE.Mesh(new THREE.PlaneGeometry(SQUARE_SIZE, SQUARE_SIZE), material);
    square.renderOrder = 10;
    node.add(square);

    // Full-opacity border so the square reads as a handle, not a stray quad.
    const borderMaterial = new THREE.LineBasicMaterial({
      color: AXIS_COLORS[normalAxis],
      opacity: IDLE_OPACITY,
      transparent: true,
      depthTest: false,
      depthWrite: false,
    });
    const border = new THREE.LineSegments(new THREE.EdgesGeometry(new THREE.PlaneGeometry(SQUARE_SIZE, SQUARE_SIZE)), borderMaterial);
    border.renderOrder = 11;
    node.add(border);

    const pick = new THREE.Mesh(new THREE.PlaneGeometry(PICK_SQUARE_SIZE, PICK_SQUARE_SIZE), this.makePickMaterial());
    this.registerPickVolume(pick, name);
    node.add(pick);

    this.addHandle({
      name,
      kind: 'scale',
      axis: AXIS_VECTORS[normalAxis].clone(),
      planeAxes: [a, b],
      node,
      materials: [
        { material, baseColor: new THREE.Color(AXIS_COLORS[normalAxis]) },
        { material: borderMaterial, baseColor: new THREE.Color(AXIS_COLORS[normalAxis]) },
      ],
    });
  }

  // ─── Internals ──────────────────────────────────────────────────

  /**
   * Mouse ray through the scene — built with Raycaster.setFromCamera, which
   * handles BOTH camera kinds (Utils.mouseToRay shoots from the camera
   * position, which is wrong for orthographic 2D views: their rays are
   * PARALLEL, so picking would only work near the screen center).
   */
  private mouseRaycaster(pixelMouse: THREE.Vector2): THREE.Raycaster {
    const camera = this.host.getCamera();
    const domElement = this.host.getDomElement();
    const ndc = new THREE.Vector2((pixelMouse.x / domElement.clientWidth) * 2 - 1, -(pixelMouse.y / domElement.clientHeight) * 2 + 1);
    const raycaster = new THREE.Raycaster();
    raycaster.setFromCamera(ndc, camera);
    return raycaster;
  }

  /** Direction from `point` toward the viewer — the view direction for
   *  orthographic cameras (parallel rays), the camera position otherwise. */
  private static towardCamera(camera: THREE.Camera, point: THREE.Vector3): THREE.Vector3 {
    if (camera.type === 'OrthographicCamera') {
      return camera.getWorldDirection(new THREE.Vector3()).negate();
    }
    return new THREE.Vector3().subVectors(camera.position, point);
  }

  /** Point-cloud surface point under the pixel cursor (the placement tools'
   *  pick), or null when the cursor misses the cloud. */
  private pointCloudHit(pixelMouse: THREE.Vector2): THREE.Vector3 | null {
    return this.host.pickSurface?.(pixelMouse) ?? null;
  }

  private raycastPickVolumes(pixelMouse: THREE.Vector2): THREE.Intersection[] {
    const raycaster = this.mouseRaycaster(pixelMouse);
    // Mode/whitelist-hidden handles must not be pickable — the raycaster
    // itself doesn't consult ancestor visibility, so filter on the node.
    const pickable = this.pickVolumes.filter((mesh) => this.handles.get(String(mesh.userData.gizmoHandle))?.node.visible ?? false);
    return raycaster.intersectObjects(pickable, false);
  }

  private setHoverHandle(handle: GizmoHandle | null): void {
    if (this.drag || this.activeHandle === handle) return;
    this.activeHandle = handle;
    for (const h of this.handles.values()) {
      const hovered = h === handle;
      for (const { material, baseColor } of h.materials) {
        material.opacity = hovered ? HOVER_OPACITY : IDLE_OPACITY;
        material.color.copy(baseColor);
        if (hovered) material.color.lerp(WHITE, HOVER_WHITEN);
      }
    }
  }

  /**
   * Spin each rotation arc around its own axis so the visible half-circle
   * faces the camera (Blender's rotate-gizmo behavior). The arc spans
   * torus-local angles [0, RING_ARC]; its midpoint direction is rotated
   * onto the camera direction projected into the ring plane.
   */
  private faceRingsToCamera(camera: THREE.Camera): void {
    const camWorld = camera.getWorldPosition(new THREE.Vector3());
    const center = this.root.getWorldPosition(new THREE.Vector3());
    const rootQuatInv = this.root.quaternion.clone().invert();
    // Camera direction in gizmo-local space (rings live in root space).
    const camLocal = camWorld.sub(center).applyQuaternion(rootQuatInv);

    for (const handle of this.handles.values()) {
      if (handle.kind !== 'rotation') continue;
      const ring = this.ringMeshes.get(handle.name);
      if (!ring) continue;
      // Project the camera direction into the ring plane (⟂ handle.axis).
      const planar = camLocal.clone().addScaledVector(handle.axis, -camLocal.dot(handle.axis));
      if (planar.lengthSq() < 1e-12) continue;
      planar.normalize();
      // Angle of that direction in the torus's local XY frame.
      const ex = new THREE.Vector3(1, 0, 0).applyQuaternion(ring.baseQuaternion);
      const ey = new THREE.Vector3(0, 1, 0).applyQuaternion(ring.baseQuaternion);
      const angle = Math.atan2(planar.dot(ey), planar.dot(ex));
      // Rotate the arc about its local normal so the midpoint hits `angle`.
      const spin = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 0, 1), angle - RING_ARC / 2);
      ring.mesh.quaternion.copy(ring.baseQuaternion).multiply(spin);
    }
  }
}
